import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Lock, Shield, CheckCircle2 } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
export function AddCardScreen() {
  const {
    goBack
  } = useNavigation();
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Format card number with spaces
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const formatted = value.replace(/(\d{4})/g, '$1 ').trim();
    if (formatted.length <= 19) {
      setCardNumber(formatted);
    }
  };
  // Format expiry with slash
  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    if (value.length <= 5) {
      setExpiry(value);
    }
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 3) {
      setCvv(value);
    }
  };
  const handleSave = () => {
    if (cardNumber.length >= 15 && expiry.length === 5 && cvv.length === 3) {
      setShowSuccess(true);
      setTimeout(() => {
        goBack();
      }, 1500);
    }
  };
  // Determine card brand
  const getCardBrand = () => {
    if (cardNumber.startsWith('4')) return 'VISA';
    if (cardNumber.startsWith('5')) return 'MASTERCARD';
    return null;
  };
  const brand = getCardBrand();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-168">
      <Header title="Add New Card" data-id="element-169" />

      <div className="p-4" data-id="element-170">
        {/* Visual Card Preview */}
        <div className="w-full aspect-[1.6/1] rounded-2xl bg-gradient-to-br from-primary-700 via-primary to-primary-400 p-6 text-white shadow-xl mb-6 relative overflow-hidden" data-id="element-171">
          {/* Decorative circles */}
          <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full blur-xl" data-id="element-172"></div>
          <div className="absolute -left-8 -bottom-8 w-24 h-24 bg-accent/20 rounded-full blur-xl" data-id="element-173"></div>

          <div className="relative z-10 h-full flex flex-col justify-between" data-id="element-174">
            <div className="flex justify-between items-start" data-id="element-175">
              {/* Chip Icon */}
              <div className="w-10 h-8 bg-gradient-to-br from-yellow-200 to-yellow-500 rounded-md opacity-90 flex items-center justify-center overflow-hidden" data-id="element-176">
                <div className="w-full h-[1px] bg-black/20 absolute" data-id="element-177"></div>
                <div className="h-full w-[1px] bg-black/20 absolute" data-id="element-178"></div>
              </div>

              {/* Brand Logo */}
              <div className="h-6 flex items-center justify-end" data-id="element-179">
                {brand ? <span className="font-bold italic text-lg tracking-wider" data-id="element-180">
                    {brand}
                  </span> : <CreditCard className="w-8 h-8 opacity-50" data-id="element-181" />}
              </div>
            </div>

            <div data-id="element-182">
              <p className="font-mono text-xl tracking-[0.15em] mb-4 opacity-90" data-id="element-183">
                {cardNumber || '•••• •••• •••• ••••'}
              </p>
              <div className="flex justify-between items-end" data-id="element-184">
                <div data-id="element-185">
                  <p className="text-[10px] opacity-70 uppercase tracking-wider mb-0.5" data-id="element-186">
                    Cardholder Name
                  </p>
                  <p className="font-medium tracking-wide truncate max-w-[180px]" data-id="element-187">
                    {cardName ? cardName.toUpperCase() : 'YOUR NAME'}
                  </p>
                </div>
                <div className="text-right" data-id="element-188">
                  <p className="text-[10px] opacity-70 uppercase tracking-wider mb-0.5" data-id="element-189">
                    Valid Thru
                  </p>
                  <p className="font-medium tracking-wide" data-id="element-190">
                    {expiry || 'MM/YY'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-50 space-y-4" data-id="element-191">
          <Input label="Card Number" placeholder="0000 0000 0000 0000" value={cardNumber} onChange={handleCardNumberChange} icon={<CreditCard className="w-4 h-4" data-id="element-193" />} inputMode="numeric" data-id="element-192" />

          <Input label="Cardholder Name" placeholder="John Doe" value={cardName} onChange={e => setCardName(e.target.value)} data-id="element-194" />

          <div className="flex gap-3" data-id="element-195">
            <div className="flex-1" data-id="element-196">
              <Input label="Expiry Date" placeholder="MM/YY" value={expiry} onChange={handleExpiryChange} inputMode="numeric" data-id="element-197" />
            </div>
            <div className="flex-1" data-id="element-198">
              <Input label="CVV" type="password" placeholder="•••" value={cvv} onChange={handleCvvChange} icon={<Lock className="w-4 h-4" data-id="element-200" />} inputMode="numeric" data-id="element-199" />
            </div>
          </div>

          <div className="pt-2 flex items-center gap-3" data-id="element-201">
            <button type="button" onClick={() => setIsDefault(!isDefault)} className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${isDefault ? 'bg-primary border-primary' : 'bg-white border-gray-300'}`} data-id="element-202">
              {isDefault && <CheckCircle2 className="w-3.5 h-3.5 text-white" data-id="element-203" />}
            </button>
            <span className="text-sm text-gray-700 font-medium" data-id="element-204">
              Save as default payment method
            </span>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-205">
          <Shield className="w-4 h-4 text-success" data-id="element-206" />
          <span data-id="element-207">Your payment info is encrypted and secure</span>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-208">
        <div className="max-w-md mx-auto" data-id="element-209">
          <Button fullWidth size="lg" onClick={handleSave} disabled={cardNumber.length < 15 || expiry.length < 5 || cvv.length < 3} className="shadow-lg shadow-primary/20" data-id="element-210">
            Save Card
          </Button>
        </div>
      </div>

      {/* Success Toast */}
      <AnimatePresence data-id="element-211">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-212">
            <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-213">
              <CheckCircle2 className="w-5 h-5 text-success" data-id="element-214" />
              <span className="text-sm font-medium" data-id="element-215">
                Card added successfully
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}